/**
 * A trigger's schedule (docs/views.md#14-queries-and-actions): five cron fields, minute, hour, day of the month, month,
 * and day of the week, in UTC, each `*`, a number, a range `a-b`, a step `*\/n` or `a-b/n`, or a list of those. As in
 * cron, a day matches when its day of the month or its day of the week does, if both are restricted.
 */
export interface Cron {
  minutes: Set<number>;
  hours: Set<number>;
  days: Set<number>;
  months: Set<number>;
  weekdays: Set<number>;
  /** Whether the day of the month and the day of the week were each restricted. */
  anyDay: boolean;
  anyWeekday: boolean;
}

const FIELDS = [
  { name: "minute", min: 0, max: 59 },
  { name: "hour", min: 0, max: 23 },
  { name: "day of the month", min: 1, max: 31 },
  { name: "month", min: 1, max: 12 },
  { name: "day of the week", min: 0, max: 7 },
] as const;

/** A schedule read from its text, or why it can't be. */
export function readCron(text: string): Cron | { error: string } {
  const parts = text.trim().split(/\s+/);
  if (parts.length !== 5)
    return { error: `A schedule is five cron fields in UTC, such as "0 6 * * 1" for Mondays at 06:00.` };
  const sets: Set<number>[] = [];
  for (const [i, part] of parts.entries()) {
    const field = FIELDS[i]!;
    const values = new Set<number>();
    for (const item of part.split(",")) {
      const match = /^(\*|(\d+)(?:-(\d+))?)(?:\/(\d+))?$/.exec(item);
      if (!match) return { error: `The schedule's ${field.name} field, ${part}, isn't cron.` };
      const step = match[4] ? Number(match[4]) : 1;
      const from = match[1] === "*" ? field.min : Number(match[2]);
      const to = match[1] === "*" ? field.max : match[3] ? Number(match[3]) : match[4] ? field.max : from;
      if (step < 1 || from < field.min || to > field.max || from > to)
        return { error: `The schedule's ${field.name} is from ${field.min} to ${field.max}.` };
      for (let value = from; value <= to; value += step)
        values.add(field.name === "day of the week" ? value % 7 : value);
    }
    sets.push(values);
  }
  return {
    minutes: sets[0]!,
    hours: sets[1]!,
    days: sets[2]!,
    months: sets[3]!,
    weekdays: sets[4]!,
    anyDay: parts[2] === "*",
    anyWeekday: parts[4] === "*",
  };
}

function dayMatches(cron: Cron, date: Date): boolean {
  if (!cron.months.has(date.getUTCMonth() + 1)) return false;
  const day = cron.days.has(date.getUTCDate()),
    weekday = cron.weekdays.has(date.getUTCDay());
  if (cron.anyDay && cron.anyWeekday) return true;
  if (cron.anyDay) return weekday;
  if (cron.anyWeekday) return day;
  return day || weekday;
}

/** The first time the schedule fires after `after` (milliseconds), within a year and a day, or null. */
export function nextFire(cron: Cron, after: number): number | null {
  const start = new Date(Math.floor(after / 60_000) * 60_000 + 60_000);
  const day = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), start.getUTCDate()));
  const hours = [...cron.hours].sort((a, b) => a - b),
    minutes = [...cron.minutes].sort((a, b) => a - b);
  for (let d = 0; d < 367; d++, day.setUTCDate(day.getUTCDate() + 1)) {
    if (!dayMatches(cron, day)) continue;
    for (const hour of hours)
      for (const minute of minutes) {
        const at = day.getTime() + hour * 3_600_000 + minute * 60_000;
        if (at >= start.getTime()) return at;
      }
  }
  return null;
}

/** The shortest gap between the schedule's firings over the next week or so, in minutes, or null when it never fires. */
export function shortestGap(cron: Cron, from: number): number | null {
  let last = nextFire(cron, from);
  if (last === null) return null;
  let gap = Number.POSITIVE_INFINITY;
  for (let i = 0; i < 200; i++) {
    const next = nextFire(cron, last);
    if (next === null) break;
    gap = Math.min(gap, (next - last) / 60_000);
    last = next;
  }
  return gap;
}
